import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { lazy, Suspense } from 'react'
import { Spin } from 'antd'
import { AuthProvider } from '@/app/AuthProvider'
import { StoreProvider } from '@/app/StoreProvider'
import { AuthGate } from '@/app/AuthGate'
import { MainLayout } from '@/layout/MainLayout'
import { LoginPage } from '@/pages/LoginPage'
import { HomePage } from '@/pages/HomePage'
import { CashPage } from '@/pages/CashPage'
import { CashDetailPage } from '@/pages/CashDetailPage'

const PreviewPage = lazy(() => import('@/pages/PreviewPage').then((m) => ({ default: m.PreviewPage })))
const PlaygroundPage = lazy(() =>
  import('@/pages/PlaygroundPage').then((m) => ({ default: m.PlaygroundPage })),
)
const PiotSetupPage = lazy(() =>
  import('@/pages/PiotSetupPage').then((m) => ({ default: m.PiotSetupPage })),
)
const ScalesPage = lazy(() => import('@/pages/ScalesPage').then((m) => ({ default: m.ScalesPage })))
const OfdSbisPage = lazy(() => import('@/pages/OfdSbisPage').then((m) => ({ default: m.OfdSbisPage })))
const IntegrationJournalPage = lazy(() =>
  import('@/pages/IntegrationJournalPage').then((m) => ({ default: m.IntegrationJournalPage })),
)
const ChzJournalPage = lazy(() =>
  import('@/pages/ChzJournalPage').then((m) => ({ default: m.ChzJournalPage })),
)
const CertificatesPage = lazy(() =>
  import('@/pages/CertificatesPage').then((m) => ({ default: m.CertificatesPage })),
)
const MtsWifiPage = lazy(() => import('@/pages/MtsWifiPage').then((m) => ({ default: m.MtsWifiPage })))
const TrassirPage = lazy(() => import('@/pages/TrassirPage').then((m) => ({ default: m.TrassirPage })))
const StatisticsPage = lazy(() =>
  import('@/pages/StatisticsPage').then((m) => ({ default: m.StatisticsPage })),
)
const WeatherPage = lazy(() =>
  import('@/pages/WeatherPage').then((m) => ({ default: m.WeatherPage })),
)
const KitKioskPage = lazy(() =>
  import('@/pages/KitKioskPage').then((m) => ({ default: m.KitKioskPage })),
)
const SettingsPage = lazy(() =>
  import('@/pages/SettingsPage').then((m) => ({ default: m.SettingsPage })),
)
const LogsPage = lazy(() => import('@/pages/LogsPage').then((m) => ({ default: m.LogsPage })))
const ProfilePage = lazy(() =>
  import('@/pages/ProfilePage').then((m) => ({ default: m.ProfilePage })),
)
const PosIncidentsPage = lazy(() =>
  import('@/pages/PosIncidentsPage').then((m) => ({ default: m.PosIncidentsPage })),
)

function LazyFallback() {
  return (
    <div style={{ display: 'grid', placeItems: 'center', minHeight: 240, padding: 48 }}>
      <Spin size="large" />
    </div>
  )
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route
            path="/preview"
            element={
              <Suspense fallback={<LazyFallback />}>
                <PreviewPage />
              </Suspense>
            }
          />
          <Route
            element={
              <StoreProvider>
                <AuthGate />
              </StoreProvider>
            }
          >
            <Route element={<MainLayout />}>
              <Route path="/" element={<HomePage />} />
              <Route path="/cash" element={<CashPage />} />
              <Route
                path="/cash/incidents"
                element={
                  <Suspense fallback={<LazyFallback />}>
                    <PosIncidentsPage />
                  </Suspense>
                }
              />
              <Route path="/cash/:id" element={<CashDetailPage />} />
              <Route
                path="/piot-setup"
                element={
                  <Suspense fallback={<LazyFallback />}>
                    <PiotSetupPage />
                  </Suspense>
                }
              />
              <Route
                path="/scales"
                element={
                  <Suspense fallback={<LazyFallback />}>
                    <ScalesPage />
                  </Suspense>
                }
              />
              <Route
                path="/ofd-sbis"
                element={
                  <Suspense fallback={<LazyFallback />}>
                    <OfdSbisPage />
                  </Suspense>
                }
              />
              <Route
                path="/journals/integration"
                element={
                  <Suspense fallback={<LazyFallback />}>
                    <IntegrationJournalPage />
                  </Suspense>
                }
              />
              <Route
                path="/journals/chz"
                element={
                  <Suspense fallback={<LazyFallback />}>
                    <ChzJournalPage />
                  </Suspense>
                }
              />
              <Route
                path="/certificates"
                element={
                  <Suspense fallback={<LazyFallback />}>
                    <CertificatesPage />
                  </Suspense>
                }
              />
              <Route
                path="/mtswifi"
                element={
                  <Suspense fallback={<LazyFallback />}>
                    <MtsWifiPage />
                  </Suspense>
                }
              />
              <Route
                path="/trassir"
                element={
                  <Suspense fallback={<LazyFallback />}>
                    <TrassirPage />
                  </Suspense>
                }
              />
              <Route
                path="/statistics"
                element={
                  <Suspense fallback={<LazyFallback />}>
                    <StatisticsPage />
                  </Suspense>
                }
              />
              <Route
                path="/weather"
                element={
                  <Suspense fallback={<LazyFallback />}>
                    <WeatherPage />
                  </Suspense>
                }
              />
              <Route
                path="/kit-kiosk"
                element={
                  <Suspense fallback={<LazyFallback />}>
                    <KitKioskPage />
                  </Suspense>
                }
              />
              <Route
                path="/settings/*"
                element={
                  <Suspense fallback={<LazyFallback />}>
                    <SettingsPage />
                  </Suspense>
                }
              />
              <Route
                path="/profile"
                element={
                  <Suspense fallback={<LazyFallback />}>
                    <ProfilePage />
                  </Suspense>
                }
              />
              <Route
                path="/logs"
                element={
                  <Suspense fallback={<LazyFallback />}>
                    <LogsPage />
                  </Suspense>
                }
              />
              <Route
                path="/playground"
                element={
                  <Suspense fallback={<LazyFallback />}>
                    <PlaygroundPage />
                  </Suspense>
                }
              />
            </Route>
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  )
}
